import React from "react";
import PropTypes from "prop-types";
import { Button, Tooltip, makeStyles, tokens } from "@fluentui/react-components";
import {
  AddRegular,
  ArrowMinimizeFilled,
  Crown16Filled,
  MicFilled,
  MicOffFilled,
  PinFilled,
  PinRegular,
  HandRight20Filled,
  SubtractRegular,
  ZoomInRegular,
  ZoomOutRegular,
  VideoPersonSparkleFilled,
  VideoPersonSparkleRegular
} from "@fluentui/react-icons";
import { SCREEN_SHARE_TILE_KIND } from "./participant-layout-participants";

const HAND_RAISED_LABEL = "Hand raised";
const ROOM_SPOTLIGHTED_BY_HOST_LABEL = "Spotlighted by host";

const useStyles = makeStyles({
  root: {
    position: "absolute",
    top: "var(--participant-tile-chrome-top, 0px)",
    right: "var(--participant-tile-chrome-right, 0px)",
    bottom: "var(--participant-tile-chrome-bottom, 0px)",
    left: "var(--participant-tile-chrome-left, 0px)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    paddingTop: "8px",
    paddingRight: "8px",
    paddingBottom: "8px",
    paddingLeft: "8px",
    pointerEvents: "none"
  },
  handRaisedBadge: {
    position: "absolute",
    top: "8px",
    left: "8px",
    display: "inline-flex",
    width: "32px",
    height: "32px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "var(--secondaryColorBackground)",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.22)",
    pointerEvents: "auto"
  },
  topRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-end",
    gap: "8px"
  },
  actionsRow: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: tokens.spacingHorizontalXS,
    maxWidth: "100%",
    minWidth: 0,
    pointerEvents: "none"
  },
  nameChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    width: "fit-content",
    maxWidth: "min(60%, 280px)",
    minWidth: 0,
    minHeight: "32px",
    paddingRight: "10px",
    paddingLeft: "10px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "var(--secondaryColorBackground)",
    color: "var(--color-text-primary)",
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 500,
    pointerEvents: "auto",
    "@media (max-width: 559px)": {
      maxWidth: "calc(100% - 16px)"
    }
  },
  nameText: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  roleIcon: {
    flexShrink: 0,
    width: "16px",
    height: "16px",
    color: "var(--color-text-primary)",
    fill: "var(--color-text-primary)"
  },
  actionTooltipTrigger: {
    display: "inline-flex",
    flexShrink: 0,
    pointerEvents: "auto"
  },
  actionButton: {
    width: "32px",
    minWidth: "32px",
    height: "32px",
    paddingRight: "0",
    paddingLeft: "0",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "var(--secondaryColorBackground)",
    color: "var(--color-text-primary)",
    pointerEvents: "auto",
    "&:hover": {
      backgroundColor: "var(--accent1-color-hover)"
    },
    "&:hover:active": {
      backgroundColor: "var(--primaryColorBackground)"
    },
    "& span": {
      width: "16px",
      height: "16px",
      fontSize: "16px"
    },
    "& svg": {
      width: "16px",
      height: "16px",
      fill: "var(--color-text-primary)",
      color: "var(--color-text-primary)"
    }
  },
  bottomRow: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: "8px"
  },
  mediaZoomSpacer: {
    flex: "1 1 auto",
    minWidth: 0
  },
  mediaZoomRow: {
    display: "inline-flex",
    alignItems: "center",
    flex: "0 0 auto",
    gap: tokens.spacingHorizontalXS,
    pointerEvents: "auto"
  },
  mediaZoomValue: {
    display: "inline-flex",
    width: "48px",
    height: "32px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "var(--secondaryColorBackground)",
    color: "var(--color-text-primary)",
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 400
  }
});

function getParticipantNameLabel(participant) {
  return participant.isSelf ? `${participant.displayName} (You)` : participant.displayName;
}

function getParticipantRoleFallbackLabel(role) {
  const fallbackRole = role ? role.replace(/^room\s+/i, "") : null;

  return fallbackRole ? `${fallbackRole[0].toUpperCase()}${fallbackRole.slice(1)}` : null;
}

export function ParticipantTileChrome({
  canZoomMedia = false,
  canDecreaseMediaZoom = false,
  canIncreaseMediaZoom = false,
  chromeStyle,
  hideFullscreenButton = false,
  hidePinButton = false,
  isFullscreenActive = false,
  isMediaZoomed = false,
  mediaZoomPercent = 100,
  participant,
  onDecreaseParticipantMediaZoom,
  onIncreaseParticipantMediaZoom,
  onRequestParticipantFullscreen,
  onRequestParticipantMute,
  onToggleParticipantMediaZoom,
  onToggleParticipantPinned,
  onToggleParticipantRoomSpotlight
}) {
  const styles = useStyles();
  const showsMicButton = participant.kind !== SCREEN_SHARE_TILE_KIND;
  const showsFullscreenButton =
    isFullscreenActive && !hideFullscreenButton && participant.isPinned && Boolean(onRequestParticipantFullscreen);
  const showsMediaZoomButton = canZoomMedia && Boolean(onToggleParticipantMediaZoom);
  const showsMediaZoomStepControls =
    showsMediaZoomButton &&
    isMediaZoomed &&
    Boolean(onDecreaseParticipantMediaZoom) &&
    Boolean(onIncreaseParticipantMediaZoom);
  const showsPinButton = !hidePinButton;
  const showsSpotlightButton = participant.canToggleRoomSpotlight && Boolean(onToggleParticipantRoomSpotlight);
  const showsRaisedHandBadge = participant.kind !== SCREEN_SHARE_TILE_KIND && participant.isHandRaised;
  const canToggleMuted = participant.canToggleMuted !== false;
  const isDisabledMicButton = !canToggleMuted || participant.isMuted;
  const mutedMicIconStyle = participant.isMuted ? { color: "var(--color-danger)", fill: "var(--color-danger)" } : undefined;
  const micIcon = participant.isMuted ? (
    <MicOffFilled style={mutedMicIconStyle} />
  ) : (
    <MicFilled style={mutedMicIconStyle} />
  );
  const pinIcon = participant.isPinned ? <PinFilled /> : <PinRegular />;
  const spotlightIcon = participant.isRoomSpotlighted ? <VideoPersonSparkleFilled /> : <VideoPersonSparkleRegular />;
  const micLabel = participant.isMuted ? "Microphone muted" : "Microphone live";
  const fullscreenLabel = "Exit fullscreen";
  const fullscreenIcon = <ArrowMinimizeFilled />;
  const mediaZoomLabel = isMediaZoomed ? "Reset media zoom" : "Zoom media";
  const mediaZoomIcon = isMediaZoomed ? <ZoomOutRegular /> : <ZoomInRegular />;
  const mediaZoomPercentLabel = `${mediaZoomPercent}%`;
  const decreaseMediaZoomLabel = "Zoom out media";
  const increaseMediaZoomLabel = "Zoom in media";
  const pinDisabled = Boolean(participant.roomSpotlightActive);
  const pinLabel = participant.isRoomSpotlighted
    ? "Spotlighted by host"
    : participant.roomSpotlightActive
    ? "Host spotlight active"
    : participant.isPinned
    ? "Participant pinned"
    : "Pin participant";
  const spotlightLabel = participant.isRoomSpotlighted ? "Remove spotlight" : "Spotlight for everyone";
  const participantNameLabel = getParticipantNameLabel(participant);
  const roleFallbackLabel = getParticipantRoleFallbackLabel(participant.role);
  const nameChipAriaLabel = [
    participantNameLabel,
    participant.isRoomSpotlighted ? ROOM_SPOTLIGHTED_BY_HOST_LABEL : null,
    roleFallbackLabel
  ]
    .filter(Boolean)
    .join(" · ");
  const pinButton = (
    <Button
      appearance="transparent"
      aria-label={pinLabel}
      aria-pressed={participant.isPinned}
      className={styles.actionButton}
      data-participant-pin-button=""
      disabled={pinDisabled}
      icon={pinIcon}
      onClick={() => onToggleParticipantPinned(participant.id)}
      title={pinLabel}
    />
  );
  const spotlightButton = (
    <Button
      appearance="transparent"
      aria-label={spotlightLabel}
      aria-pressed={participant.isRoomSpotlighted}
      className={styles.actionButton}
      data-participant-spotlight-button=""
      data-participant-spotlight-tooltip-trigger=""
      icon={spotlightIcon}
      onClick={() => onToggleParticipantRoomSpotlight(participant.id)}
      title={spotlightLabel}
    />
  );

  return (
    <div className={styles.root} data-participant-tile-chrome="" style={chromeStyle}>
      {showsRaisedHandBadge && (
        <div
          aria-label={HAND_RAISED_LABEL}
          className={styles.handRaisedBadge}
          data-participant-hand-raised-badge=""
          title={HAND_RAISED_LABEL}
        >
          <HandRight20Filled aria-hidden="true" />
        </div>
      )}
      <div className={styles.topRow}>
        <div className={styles.actionsRow} data-participant-actions-row="">
          {showsMicButton && (
            <Button
              appearance="transparent"
              aria-label={micLabel}
              aria-pressed={participant.isMuted}
              className={styles.actionButton}
              data-participant-mic-button=""
              disabled={isDisabledMicButton}
              icon={micIcon}
              onClick={() => onRequestParticipantMute(participant)}
              title={micLabel}
            />
          )}
          {showsSpotlightButton && (
            <Tooltip content={spotlightLabel} relationship="description">
              {spotlightButton}
            </Tooltip>
          )}
          {showsPinButton &&
            (pinDisabled ? (
              <Tooltip content={pinLabel} relationship="description">
                <span
                  aria-disabled="true"
                  aria-label={pinLabel}
                  className={styles.actionTooltipTrigger}
                  data-participant-pin-tooltip-trigger=""
                  role="button"
                  tabIndex={0}
                  title={pinLabel}
                >
                  {pinButton}
                </span>
              </Tooltip>
            ) : (
              pinButton
            ))}
          {showsFullscreenButton && (
            <Button
              appearance="transparent"
              aria-label={fullscreenLabel}
              aria-pressed={isFullscreenActive}
              className={styles.actionButton}
              data-participant-fullscreen-button=""
              icon={fullscreenIcon}
              onClick={() => onRequestParticipantFullscreen(participant)}
              title={fullscreenLabel}
            />
          )}
        </div>
      </div>
      <div className={styles.bottomRow} data-participant-media-zoom-row="" data-participant-name-row="">
        <Tooltip content={nameChipAriaLabel} relationship="description">
          <div aria-label={nameChipAriaLabel} className={styles.nameChip} data-participant-name-chip="">
            <span className={styles.nameText} data-participant-name-text="">
              {participantNameLabel}
            </span>
            {participant.isRoomSpotlighted && (
              <VideoPersonSparkleFilled
                aria-label={ROOM_SPOTLIGHTED_BY_HOST_LABEL}
                className={styles.roleIcon}
                data-participant-spotlight-status-icon=""
                title={ROOM_SPOTLIGHTED_BY_HOST_LABEL}
              />
            )}
            {roleFallbackLabel && (
              <Crown16Filled
                aria-label={roleFallbackLabel}
                className={styles.roleIcon}
                data-participant-role-icon=""
                title={roleFallbackLabel}
              />
            )}
          </div>
        </Tooltip>
        <div className={styles.mediaZoomSpacer} />
        {showsMediaZoomButton && (
          <div className={styles.mediaZoomRow}>
            {showsMediaZoomStepControls && (
              <>
                <Button
                  appearance="transparent"
                  aria-label={decreaseMediaZoomLabel}
                  className={styles.actionButton}
                  data-participant-media-zoom-decrease-button=""
                  disabled={!canDecreaseMediaZoom}
                  icon={<SubtractRegular />}
                  onClick={onDecreaseParticipantMediaZoom}
                  title={decreaseMediaZoomLabel}
                />
                <span
                  aria-label={`Media zoom ${mediaZoomPercentLabel}`}
                  className={styles.mediaZoomValue}
                  data-participant-media-zoom-value=""
                  title={mediaZoomPercentLabel}
                >
                  {mediaZoomPercentLabel}
                </span>
                <Button
                  appearance="transparent"
                  aria-label={increaseMediaZoomLabel}
                  className={styles.actionButton}
                  data-participant-media-zoom-increase-button=""
                  disabled={!canIncreaseMediaZoom}
                  icon={<AddRegular />}
                  onClick={onIncreaseParticipantMediaZoom}
                  title={increaseMediaZoomLabel}
                />
              </>
            )}
            <Button
              appearance="transparent"
              aria-label={mediaZoomLabel}
              aria-pressed={isMediaZoomed}
              className={styles.actionButton}
              data-participant-media-zoom-button=""
              icon={mediaZoomIcon}
              onClick={onToggleParticipantMediaZoom}
              title={mediaZoomLabel}
            />
          </div>
        )}
      </div>
    </div>
  );
}

ParticipantTileChrome.propTypes = {
  canZoomMedia: PropTypes.bool,
  canDecreaseMediaZoom: PropTypes.bool,
  canIncreaseMediaZoom: PropTypes.bool,
  chromeStyle: PropTypes.object,
  hideFullscreenButton: PropTypes.bool,
  hidePinButton: PropTypes.bool,
  isFullscreenActive: PropTypes.bool,
  isMediaZoomed: PropTypes.bool,
  mediaZoomPercent: PropTypes.number,
  participant: PropTypes.shape({
    id: PropTypes.string.isRequired,
    kind: PropTypes.string,
    displayName: PropTypes.string.isRequired,
    role: PropTypes.string,
    canToggleMuted: PropTypes.bool,
    canToggleRoomSpotlight: PropTypes.bool,
    isMuted: PropTypes.bool,
    isPinned: PropTypes.bool,
    isRoomSpotlighted: PropTypes.bool,
    roomSpotlightActive: PropTypes.bool,
    isSelf: PropTypes.bool,
    isHandRaised: PropTypes.bool
  }).isRequired,
  onDecreaseParticipantMediaZoom: PropTypes.func,
  onIncreaseParticipantMediaZoom: PropTypes.func,
  onRequestParticipantFullscreen: PropTypes.func,
  onRequestParticipantMute: PropTypes.func.isRequired,
  onToggleParticipantMediaZoom: PropTypes.func,
  onToggleParticipantPinned: PropTypes.func.isRequired,
  onToggleParticipantRoomSpotlight: PropTypes.func
};
